import { useState } from 'react'
import type { JournalEntry, JournalEntryInsert } from '../lib/supabase'
import { JournalEntryRow } from './JournalEntryRow'
import { TradeJournalEntry } from './TradeJournalEntry'
import { JournalAnalytics } from './JournalAnalytics'
import { StrategyView } from './StrategyView'

type SleeveFilter = 'all' | 'etf' | 'stock' | 'ipo'

interface Props {
  entries: JournalEntry[]
  onSave: (entry: Partial<JournalEntryInsert> & { id?: number }) => void
  prefillTicker?: string | null
  onClearPrefill?: () => void
  sleeveMap?: Record<string, string>
}

type ViewMode = 'list' | 'analytics' | 'strategy'

export function TradeJournal({ entries, onSave, prefillTicker, onClearPrefill, sleeveMap = {} }: Props) {
  const [view, setView] = useState<ViewMode>('list')
  const [panelOpen, setPanelOpen] = useState(false)
  const [editEntry, setEditEntry] = useState<JournalEntry | null>(null)
  const [filterSleeve, setFilterSleeve] = useState<SleeveFilter>('all')

  const handleNewEntry = () => {
    setEditEntry(null)
    setPanelOpen(true)
  }

  const handleEditEntry = (entry: JournalEntry) => {
    setEditEntry(entry)
    setPanelOpen(true)
  }

  const handleClose = () => {
    setPanelOpen(false)
    setEditEntry(null)
    onClearPrefill?.()
  }

  // Open panel when prefillTicker is set
  if (prefillTicker && !panelOpen) {
    setPanelOpen(true)
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Trade Journal ({entries.length})</h2>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
          <div className="journal-view-toggle">
            <button
              className={view === 'list' ? 'active' : ''}
              onClick={() => setView('list')}
            >
              List
            </button>
            <button
              className={view === 'analytics' ? 'active' : ''}
              onClick={() => setView('analytics')}
            >
              Analytics
            </button>
            <button
              className={view === 'strategy' ? 'active' : ''}
              onClick={() => setView('strategy')}
            >
              Strategy
            </button>
          </div>
          <button className="journal-btn primary" onClick={handleNewEntry}>
            + New Entry
          </button>
        </div>
      </div>

      {view === 'list' && (
        <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-md)', flexWrap: 'wrap' }}>
          {(['all', 'etf', 'stock', 'ipo'] as SleeveFilter[]).map(sl => {
            const count = sl === 'all'
              ? entries.length
              : entries.filter(e => e.ticker && (sleeveMap[e.ticker] || '').toLowerCase() === sl).length
            return (
              <button
                key={sl}
                className="card-badge"
                onClick={() => setFilterSleeve(sl)}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  background: filterSleeve === sl
                    ? sl === 'all' ? 'var(--accent-cyan-glow)' : `var(--sleeve-${sl})20`
                    : 'var(--bg-tertiary)',
                  color: filterSleeve === sl
                    ? sl === 'all' ? 'var(--accent-cyan)' : `var(--sleeve-${sl})`
                    : 'var(--text-muted)',
                }}
              >
                {sl === 'all' ? `ALL (${count})` : `${sl.toUpperCase()} (${count})`}
              </button>
            )
          })}
        </div>
      )}

      {view === 'strategy' ? (
        <StrategyView />
      ) : view === 'list' ? (
        (() => {
          const filtered = filterSleeve === 'all'
            ? entries
            : entries.filter(e => e.ticker && (sleeveMap[e.ticker] || '').toLowerCase() === filterSleeve)
          return filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-text">No journal entries yet. Start by adding a trade review.</div>
            </div>
          ) : (
            <div>
              {filtered.map(entry => (
                <JournalEntryRow key={entry.id} entry={entry} onClick={handleEditEntry} />
              ))}
            </div>
          )
        })()
      ) : (
        <JournalAnalytics entries={entries} />
      )}

      <TradeJournalEntry
        entry={editEntry}
        prefillTicker={prefillTicker}
        open={panelOpen}
        onClose={handleClose}
        onSave={onSave}
      />
    </div>
  )
}
